import {Component, Input, OnInit} from '@angular/core';
import {HgGdvPartnerBaseComponent} from "../hg-gdv-partner-base.component";
import {NavigationItem} from "../../../../../../../core/shared/dto/navigation/navigation-item";
import {HgPartnerDto} from "../../../../../model/dto/entity/hg-partner.dto";
import {WidgetData} from "../../../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../../../core/shared/misc/sorting";
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from "../../../../../../../core/shared/helper/widget-function-call-back-create";
import {ObjectIdentifierData} from "../../../../../../../core/shared/basic/object-identifier-data";
import {ObjectIdentifier} from "../../../../../../../core/shared/basic/object-identifier";
import {MvsObjectNavigationEntry} from "../../../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {MvsObjectNavigationService} from "../../../../../../../core/shared/navigation/mvs-object-navigation.service";
import {ActivatedRoute, Router} from "@angular/router";
import {HgPartnerService} from "../../../../../service/api/hg-partner.service";
import {MvsMessageService} from "../../../../../../../core/shared/service/message/mvs-message.service";
import {EiImportService} from "../../../../../../ei/service/api/ei-import.service";

@Component({
    selector: 'hg-gdv-partner-full',
    templateUrl: 'hg-gdv-partner-full.component.html',
    standalone: false
})
export class HgGdvPartnerFullComponent extends HgGdvPartnerBaseComponent implements OnInit {

    @Input() dto: HgPartnerDto;

    // Lazy-initialized child widgets (CONFIG-022)
    partnerPartnersWidget: WidgetData;
    exportSatzartenWidget: WidgetData;
    flowOverridesWidget: WidgetData;

    navigationItems: NavigationItem[] = [
        {
            label: 'gdvPartner',
            action: 'gdvPartner',
            icon: 'fa-solid fa-info-circle',
            tooltip: 'GDV Partner',
            toggleable: false,
            default: true
        },
        {
            label: 'test',
            action: 'test',
            icon: 'fa-solid fa-vials',
            tooltip: 'Test',
            toggleable: false,
            default: false
        },
        {
            label: 'GDV-Importe',
            action: 'imports',
            icon: 'fa-solid fa-file-import',
            tooltip: 'GDV-Importe',
            toggleable: false,
            default: false
        },
        {
            label: 'Partner-Zuordnungen',
            action: 'partnerPartners',
            icon: 'fa-solid fa-link',
            tooltip: 'Partner-Zuordnungen',
            toggleable: false,
            default: false
        },
        {
            label: 'Export-Satzarten',
            action: 'exportSatzarten',
            icon: 'fa-solid fa-file-export',
            tooltip: 'Export-Satzarten',
            toggleable: false,
            default: false
        },
        {
            label: 'Flow-Overrides',
            action: 'flowOverrides',
            icon: 'fa-solid fa-code-branch',
            tooltip: 'Partner-spezifische Logic Flow Overrides',
            toggleable: false,
            default: false
        }
    ];

    constructor(
        protected override route: ActivatedRoute,
        protected override hgPartnerService: HgPartnerService,
        protected override messageService: MvsMessageService,
        protected override router: Router,
        protected override eiImportService: EiImportService,
        protected navigationService: MvsObjectNavigationService) {
        super(route, hgPartnerService, messageService, router, eiImportService);
    }

    /**
     * Whether the "Schema einrichten" dialog is currently visible.
     *
     * @ticket TM-1000001721
     */
    schemaSetupDialogVisible: boolean = false;

    ngOnInit(): void {
        super.ngOnInit();
    }

    /**
     * Opens the combined "Schema einrichten" dialog.
     *
     * @ticket TM-1000001721
     */
    openSchemaSetupDialog(): void {
        this.schemaSetupDialogVisible = true;
    }

    /**
     * Called when the schema-setup dialog reports a successful change.
     * Updates the locally-held partner DTO and rebuilds the widgets so the new
     * schemaDtoId is reflected. The parent OBC owns the canonical DTO; emitting
     * up to the parent is not necessary for this mode view because the dialog
     * already received the freshly-loaded DTO from the backend.
     *
     * @ticket TM-1000001721
     */
    handleSchemaUpdated(updated: HgPartnerDto): void {
        this.dto = updated;
        this.refreshWidgets();
    }

    /**
     * CONFIG-022: Lazy-init child widgets when the matching nav section is activated.
     */
    override onNavigationItemChange(navigationItem: NavigationItem) {
        if (!navigationItem || !this.dto?.id) {
            return;
        }

        switch (navigationItem.action) {
            case 'partnerPartners':
                if (!this.partnerPartnersWidget) {
                    this.buildPartnerPartnersWidget();
                }
                break;
            case 'exportSatzarten':
                if (!this.exportSatzartenWidget) {
                    this.buildExportSatzartenWidget();
                }
                break;
            case 'flowOverrides':
                if (!this.flowOverridesWidget) {
                    this.buildFlowOverridesWidget();
                }
                break;
        }
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: WidgetConfig
     * Reason: Partner-Zuordnungen Tabelle - Child von HgPartner mit FK-Defaulting
     */
    private buildPartnerPartnersWidget() {
        this.partnerPartnersWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'hg.partner.object.partnerPartners',
            'Partner-Zuordnungen',
            'hg.HgPartnerPartner',
            [FilterCriteria.create('e.hgPartnerId', FilterCriteria.cOperatorEqual, this.dto.id)],
            [new Sorting('id', false)]
        );
        const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'hgPartnerDtoId', fieldValue: this.dto.id}];
        this.partnerPartnersWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: WidgetConfig
     * Reason: Export-Satzarten Tabelle - Child von HgPartner mit FK-Defaulting
     */
    private buildExportSatzartenWidget() {
        this.exportSatzartenWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'hg.partner.object.exportSatzarten',
            'Export-Satzarten',
            'hg.HgExportSatzart',
            [FilterCriteria.create('e.hgPartnerId', FilterCriteria.cOperatorEqual, this.dto.id)],
            [new Sorting('id', false)]
        );
        const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'hgPartnerDtoId', fieldValue: this.dto.id}];
        this.exportSatzartenWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: WidgetConfig
     * Reason: Flow-Overrides Tabelle - Child von HgPartner mit FK-Defaulting (partnerDtoId)
     */
    private buildFlowOverridesWidget() {
        this.flowOverridesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'hg.partner.object.flowOverrides',
            'Flow-Overrides',
            'hg.HgPartnerFlowOverride',
            [FilterCriteria.create('e.partnerId', FilterCriteria.cOperatorEqual, this.dto.id)],
            [new Sorting('id', false)]
        );
        const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'partnerDtoId', fieldValue: this.dto.id}];
        this.flowOverridesWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    /**
     * Navigate to child entity detail when a row is clicked.
     */
    handleChildObjectSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            event.objectType, event.objectId, 'object', '', '', '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

}
